/**
 * Editor state for the structured pipeline editor.
 *
 * "mode" is derived state — never user-toggled. Picking a preset puts the
 * editor in 'preset' mode; any edit auto-flips to 'modified'. The user
 * can flip to 'upload' to use the existing file-upload affordance, and
 * back. There's no 'custom' radio button — modification IS the custom mode.
 */

import { useCallback, useMemo, useState } from 'react';

import { inferModuleLlmOverride } from './moduleLlmOverride';
import type {
  EditorMode,
  ModuleRef,
  PipelineDoc,
  PipelineManifest,
  StageDoc,
} from './types';
import { toModuleRefObject } from './types';

/**
 * Normalize every stage's modules from the YAML mixed-form
 * (string | object) into the object form. The editor's internal
 * mutations expect objects so it can safely spread overrides like
 * `llm`. YAML round-trip semantics are preserved on save because
 * js-yaml emits a plain object as a mapping either way.
 */
function normalizeDoc(doc: PipelineDoc): PipelineDoc {
  return {
    ...doc,
    pipeline: doc.pipeline.map(stage => ({
      ...stage,
      modules: stage.modules.map(toModuleRefObject),
    })),
  };
}

interface UsePipelineEditorStateArgs {
  manifest: PipelineManifest;
  /** Initial preset name to load. Falls back to the first preset. */
  initialPresetName?: string;
}

export interface PipelineEditorState {
  doc: PipelineDoc;
  basePresetName: string | null;
  mode: EditorMode;
  isUploadMode: boolean;

  selectPreset: (name: string) => void;
  resetToPreset: () => void;
  setDoc: (next: PipelineDoc | ((prev: PipelineDoc) => PipelineDoc)) => void;
  replaceModule: (
    stageIndex: number,
    moduleIndex: number,
    next: ModuleRef
  ) => void;
  appendModule: (stageIndex: number, mod: ModuleRef) => void;
  removeModule: (stageIndex: number, moduleIndex: number) => void;
  /**
   * Set a single config knob for a module. Writes into
   * `doc.module_config[moduleName][knobName]`. Passing `undefined` for
   * value clears the override (next read falls back to the manifest's
   * config_knobs default via buildModuleConfig).
   */
  setModuleConfig: (
    moduleName: string,
    knobName: string,
    value: string | number | boolean | string[] | null | undefined
  ) => void;
  setUploadMode: (on: boolean) => void;
}

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function findPreset(manifest: PipelineManifest, name: string | null) {
  if (!name) return null;
  return manifest.presets.find(p => p.name === name) ?? null;
}

const EMPTY_PIPELINE_DOC: PipelineDoc = {
  llm: { models: {} },
  pipeline: [],
};

const DEFAULT_PRESET_NAME = 'premium-bug-rush';

export function usePipelineEditorState({
  manifest,
  initialPresetName,
}: UsePipelineEditorStateArgs): PipelineEditorState {
  const initialPreset =
    findPreset(manifest, initialPresetName ?? null) ??
    findPreset(manifest, DEFAULT_PRESET_NAME) ??
    manifest.presets[0] ??
    null;

  const [basePresetName, setBasePresetName] = useState<string | null>(
    initialPreset?.name ?? null
  );

  const [doc, setDocState] = useState<PipelineDoc>(() =>
    initialPreset ? deepClone(initialPreset.doc) : EMPTY_PIPELINE_DOC
  );

  const [isUploadMode, setIsUploadMode] = useState(false);

  // Pristine snapshot of the current base preset. Stored in state (not a
  // ref) so reads from useMemo / render are valid. Updates only fire when
  // the user picks a preset — not on every doc edit — so re-render cost
  // is negligible.
  const [pristine, setPristine] = useState<PipelineDoc | null>(() =>
    initialPreset ? deepClone(initialPreset.doc) : null
  );

  const mode: EditorMode = useMemo(() => {
    if (isUploadMode) return 'upload';
    if (!basePresetName) return 'custom';
    if (!pristine) return 'custom';
    return deepEqual(doc, pristine) ? 'preset' : 'modified';
  }, [doc, basePresetName, isUploadMode, pristine]);

  const selectPreset = useCallback(
    (name: string) => {
      const preset = findPreset(manifest, name);
      if (!preset) return;
      setPristine(normalizeDoc(deepClone(preset.doc)));
      setBasePresetName(preset.name);
      setDocState(normalizeDoc(deepClone(preset.doc)));
    },
    [manifest]
  );

  const resetToPreset = useCallback(() => {
    if (!pristine) return;
    setDocState(normalizeDoc(deepClone(pristine)));
  }, [pristine]);

  const setDoc = useCallback<PipelineEditorState['setDoc']>(next => {
    setDocState(prev =>
      typeof next === 'function'
        ? (next as (p: PipelineDoc) => PipelineDoc)(prev)
        : next
    );
  }, []);

  const mutateStage = useCallback(
    (stageIndex: number, fn: (stage: StageDoc) => StageDoc) => {
      setDocState(prev => {
        const stage = prev.pipeline[stageIndex];
        if (!stage) return prev;
        const nextStage = fn(stage);
        const nextPipeline = prev.pipeline.map((s, i) =>
          i === stageIndex ? nextStage : s
        );
        return { ...prev, pipeline: nextPipeline };
      });
    },
    []
  );

  const replaceModule = useCallback<PipelineEditorState['replaceModule']>(
    (stageIndex, moduleIndex, next) => {
      mutateStage(stageIndex, stage => ({
        ...stage,
        modules: stage.modules.map((m, i) => (i === moduleIndex ? next : m)),
      }));
    },
    [mutateStage]
  );

  const appendModule = useCallback<PipelineEditorState['appendModule']>(
    (stageIndex, mod) => {
      mutateStage(stageIndex, stage => ({
        ...stage,
        modules: [...stage.modules, mod],
      }));
    },
    [mutateStage]
  );

  const removeModule = useCallback<PipelineEditorState['removeModule']>(
    (stageIndex, moduleIndex) => {
      mutateStage(stageIndex, stage => ({
        ...stage,
        modules: stage.modules.filter((_, i) => i !== moduleIndex),
      }));
    },
    [mutateStage]
  );

  const setModuleConfig = useCallback<PipelineEditorState['setModuleConfig']>(
    (moduleName, knobName, value) => {
      setDocState(prev => {
        const existing = prev.module_config?.[moduleName] ?? {};
        let nextKnobs: Record<string, unknown>;
        if (value === undefined) {
          // Clear override — fall back to manifest default on next read.
          // Rebuild via filter rather than rest-destructure to avoid an
          // unused-binding lint complaint on the elided key.
          nextKnobs = Object.fromEntries(
            Object.entries(existing).filter(([k]) => k !== knobName)
          );
        } else {
          nextKnobs = { ...existing, [knobName]: value };
        }

        // Per-module llm override for cross-provider scoring_model picks.
        // Constantine's pipeline runner applies a module-level `llm:` block
        // as a scoped override for that module's chat() calls only. Without
        // this injection, bare scoring_model values like
        // `bedrock/moonshotai.kimi-k2.5` route to /anthropic/v1/messages and
        // bifrost 500s. We update the matching ModuleRefObject in-place
        // whenever scoring_model is set, and clear it when the new value
        // is same-provider as the top-level (the override is redundant
        // there and noise in the YAML).
        let nextPipeline = prev.pipeline;
        if (knobName === 'scoring_model') {
          const desiredOverride =
            value === undefined || value === null
              ? null
              : inferModuleLlmOverride(prev.llm, String(value));
          nextPipeline = prev.pipeline.map(stage => {
            const stageModules = stage.modules.map(mod => {
              const refObj = toModuleRefObject(mod);
              if (refObj.name !== moduleName) return mod;
              if (desiredOverride) {
                return { ...refObj, llm: desiredOverride };
              }
              // No override needed — strip any previously-emitted llm block.
              if (refObj.llm === undefined) return mod;
              const { llm: _omit, ...rest } = refObj;
              void _omit;
              return rest;
            });
            return { ...stage, modules: stageModules };
          });
        }

        return {
          ...prev,
          pipeline: nextPipeline,
          module_config: {
            ...(prev.module_config ?? {}),
            [moduleName]: nextKnobs,
          },
        };
      });
    },
    []
  );

  const setUploadMode = useCallback((on: boolean) => {
    setIsUploadMode(on);
  }, []);

  return {
    doc,
    basePresetName,
    mode,
    isUploadMode,
    selectPreset,
    resetToPreset,
    setDoc,
    replaceModule,
    appendModule,
    removeModule,
    setModuleConfig,
    setUploadMode,
  };
}
